// Generic tool page: drop zone → workspace (tool view + options) →
// processing → results. Each tool only provides a ToolView.
import { engineName } from '../app/engines';
import { takeHandoff, setHandoff } from '../app/handoff';
import { fileFromBytes, ingestFiles } from '../app/inputs';
import { navigate, setNavigationGuard } from '../app/router';
import { OFFICE_ENABLED, netserenoLink, resourcesLink } from '../config';
import { fmt, formatBytes, formatPercent, t } from '../i18n';
import { officeSupported } from '../lib/engines/office';
import { releasePdf } from '../lib/engines/pdfjs';
import { downloadSize } from '../lib/engines/vendor';
import { downloadFile } from '../lib/download';
import { MIME, acceptAttribute, formatList, zipFiles } from '../lib/files';
import { CancelledError, InvalidPdfError, PasswordRequiredError, UserError, WrongPasswordError } from '../lib/pdf/errors';
import { type Lang, type ToolMeta, enabledTools, homePath, toolPath } from '../registry';
import { loadTool } from '../tools';
import type { InputFile, ToolResult, ToolView } from '../tools/types';
import { h } from './dom';
import { note, progressView, toast } from './feedback';
import { icon } from './icons';
import { ThumbnailLoader } from './thumbs';

export interface PageHandle {
  el: HTMLElement;
  dispose(): void;
}

export function errorMessage(err: unknown): string {
  const d = t();
  if (err instanceof CancelledError || (err instanceof DOMException && err.name === 'AbortError')) return '';
  if (err instanceof UserError) return err.message;
  if (err instanceof WrongPasswordError || err instanceof PasswordRequiredError) return d.errors.wrongPassword;
  if (err instanceof InvalidPdfError) return fmt(d.errors.invalidPdf, { name: 'PDF' });
  const message = err instanceof Error ? err.message : String(err);
  if (err instanceof RangeError || /out of memory|allocation failed|Array buffer allocation/i.test(message)) return d.errors.outOfMemory;
  return fmt(d.errors.generic, { message });
}

export function toolPage(meta: ToolMeta, lang: Lang): PageHandle {
  const d = t();
  const td = d.tools[meta.id];
  const thumbs = new ThumbnailLoader();
  const viewPromise = loadTool(meta.id);
  let files: InputFile[] = [];
  let view: ToolView | null = null;
  let busy = false;
  let runnable = true;
  let disposed = false;

  const stage = h('div', { class: 'container tool-body' });
  const input = h('input', { type: 'file', accept: acceptAttribute(meta.accept), multiple: meta.multiple, hidden: true });
  const pickFiles = () => input.click();

  const actionBtn = h('button', { class: 'btn btn-primary btn-lg btn-block', type: 'button' }, td.action, icon('arrow-right'));
  const sidebarStatus = h('div');
  const actionBar = h('div', { class: 'action-bar sticky-mobile' });

  const env = {
    meta,
    d,
    lang,
    thumbs,
    files: () => files,
    setFiles: (next: InputFile[]) => setFiles(next),
    pickFiles,
    setRunnable: (value: boolean) => {
      runnable = value;
      paintAction();
    },
  };

  function paintAction() {
    const minFiles = meta.minFiles ?? 1;
    actionBtn.disabled = busy || !runnable || files.length < minFiles;
  }

  // ---------- Empty state ----------
  function renderEmpty() {
    const engineNote =
      meta.engine === 'office' && !officeSupported()
        ? note(d.errors.browserUnsupported, 'error')
        : meta.engine
          ? note(fmt(d.engines.needDownloadText, { engine: engineName(meta.engine), size: formatBytes(downloadSize(meta.engine)) }))
          : null;
    const dz = h(
      'div',
      { class: `dropzone cat-${meta.category}` },
      h(
        'div',
        { style: { display: 'grid', gap: '12px', justifyItems: 'center' } },
        h('div', { class: 'dz-icon', 'aria-hidden': 'true' }, icon('upload')),
        h('button', { class: 'btn btn-primary btn-lg', type: 'button', onclick: pickFiles }, icon('file-plus'), meta.multiple ? d.common.selectFiles : d.common.selectFile),
        h('p', { class: 'dz-hint' }, meta.multiple ? d.common.dropHere : d.common.dropHereOne),
        h('p', { class: 'dz-formats' }, fmt(d.common.accepted, { formats: formatList(meta.accept) }), ' · ', d.common.pasteHint),
        h('span', { class: 'local-notice' }, icon('shield-check'), d.common.localNotice),
      ),
    );
    stage.replaceChildren(dz, engineNote ? h('div', { style: { marginTop: '16px' } }, engineNote) : '', input);
  }

  // ---------- Workspace ----------
  async function ensureView(): Promise<ToolView> {
    if (view) return view;
    const factory = await viewPromise;
    view = factory(env);
    return view;
  }

  function renderWorkspace(v: ToolView) {
    actionBar.replaceChildren(actionBtn);
    if (meta.multiple) actionBar.append(h('button', { class: 'btn btn-outline btn-block', type: 'button', onclick: pickFiles }, icon('plus'), d.common.addMore));
    actionBar.append(h('button', { class: 'btn btn-ghost btn-block', type: 'button', onclick: () => setFiles([]) }, icon('x'), d.common.startOver));
    const sidebar = h(
      'aside',
      { class: 'sidebar' },
      h(
        'div',
        { class: 'panel' },
        v.options && h('h2', { class: 'panel-title' }, d.common.options),
        v.options,
        sidebarStatus,
        h('div', { style: { marginTop: v.options ? '18px' : '0' } }, actionBar),
      ),
      h('span', { class: 'local-notice', style: { justifySelf: 'start' } }, icon('shield-check'), d.common.localNotice),
    );
    stage.replaceChildren(h('div', { class: 'workspace' }, h('section', { class: 'panel', 'aria-label': d.common.files }, v.main), sidebar), input);
    paintAction();
  }

  async function setFiles(next: InputFile[]) {
    const removed = files.filter((f) => !next.includes(f));
    files = next;
    for (const f of removed) void releasePdf(f.bytes);
    if (files.length === 0) {
      view?.dispose?.();
      view = null;
      sidebarStatus.replaceChildren();
      renderEmpty();
      return;
    }
    const v = await ensureView();
    if (disposed) return;
    if (!stage.querySelector('.workspace')) renderWorkspace(v);
    await v.update(files);
    paintAction();
  }

  async function addFiles(list: File[]) {
    if (!list.length || busy) return;
    let added = await ingestFiles(list, meta);
    if (disposed || !added.length) return;
    if (!meta.multiple) {
      if (added.length > 1 || files.length) toast(fmt(d.common.onlyOne, { name: added[0].name }), 'info');
      added = added.slice(0, 1);
      await setFiles(added);
    } else {
      await setFiles([...files, ...added]);
    }
  }

  input.addEventListener('change', () => {
    const list = Array.from(input.files ?? []);
    input.value = '';
    void addFiles(list);
  });

  // Drag & drop anywhere on the tool area, paste from the clipboard.
  let dragDepth = 0;
  const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');
  stage.addEventListener('dragenter', (e) => {
    if (!hasFiles(e)) return;
    dragDepth++;
    stage.querySelector('.dropzone')?.classList.add('drag');
  });
  stage.addEventListener('dragleave', (e) => {
    if (!hasFiles(e)) return;
    dragDepth = Math.max(0, dragDepth - 1);
    if (!dragDepth) stage.querySelector('.dropzone')?.classList.remove('drag');
  });
  stage.addEventListener('dragover', (e) => {
    if (hasFiles(e)) e.preventDefault();
  });
  stage.addEventListener('drop', (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    dragDepth = 0;
    stage.querySelector('.dropzone')?.classList.remove('drag');
    void addFiles(Array.from(e.dataTransfer?.files ?? []));
  });
  const onPaste = (e: ClipboardEvent) => {
    const pasted = Array.from(e.clipboardData?.files ?? []);
    if (pasted.length && !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)) {
      e.preventDefault();
      void addFiles(pasted);
    }
  };
  document.addEventListener('paste', onPaste);

  // ---------- Run ----------
  const beforeUnload = (e: BeforeUnloadEvent) => {
    e.preventDefault();
  };

  actionBtn.addEventListener('click', async () => {
    if (!view || busy) return;
    const minFiles = meta.minFiles ?? 1;
    if (files.length < minFiles) {
      toast(fmt(d.common.minFiles, { n: minFiles }), 'warn');
      return;
    }
    const problem = view.validate?.();
    if (problem) {
      sidebarStatus.replaceChildren(h('div', { style: { marginTop: '14px' } }, note(problem, 'error')));
      return;
    }
    busy = true;
    paintAction();
    const progress = progressView();
    actionBar.replaceChildren(progress.el);
    sidebarStatus.replaceChildren();
    stage.querySelector('.workspace > section')?.setAttribute('aria-busy', 'true');
    window.addEventListener('beforeunload', beforeUnload);
    setNavigationGuard(() => !busy);
    const controller = new AbortController();
    const started = performance.now();
    try {
      const result = await view.run((fraction, message) => progress.set(fraction, message), controller.signal);
      if (disposed) return;
      renderResults(result, (performance.now() - started) / 1000);
    } catch (err) {
      if (!(err instanceof CancelledError)) console.error(err);
      if (!disposed) {
        renderWorkspace(view);
        const message = errorMessage(err);
        if (message) sidebarStatus.replaceChildren(h('div', { style: { marginTop: '14px' } }, note(message, 'error')));
      }
    } finally {
      busy = false;
      window.removeEventListener('beforeunload', beforeUnload);
      setNavigationGuard(null);
      stage.querySelector('.workspace > section')?.removeAttribute('aria-busy');
      paintAction();
    }
  });

  // ---------- Results ----------
  function renderResults(result: ToolResult, seconds: number) {
    const outputs = result.files;
    const total = outputs.reduce((s, f) => s + f.bytes.length, 0);
    const zipName = `${(files[0]?.name ?? 'netsereno').replace(/\.[^.]+$/, '')}_${meta.slugs[lang]}.zip`;
    const items = outputs.map((f) =>
      h(
        'li',
        { class: 'result-item' },
        icon(f.mime === MIME.pdf ? 'file' : f.mime.startsWith('image/') ? 'image' : 'file-text'),
        h('div', { class: 'file-info' }, h('div', { class: 'file-name', title: f.name }, f.name), h('div', { class: 'file-meta' }, formatBytes(f.bytes.length))),
        h('button', { class: 'btn btn-sm btn-outline', type: 'button', onclick: () => downloadFile(f) }, icon('download'), d.common.download),
      ),
    );
    const primary =
      outputs.length === 1
        ? h('button', { class: 'btn btn-accent btn-lg', type: 'button', autofocus: true, onclick: () => downloadFile(outputs[0]) }, icon('download'), d.common.download)
        : h('button', { class: 'btn btn-accent btn-lg', type: 'button', autofocus: true, onclick: () => downloadFile(zipFiles(outputs, zipName)) }, icon('download'), d.common.downloadAll);

    const stats: string[] = [];
    if (result.sizeBefore && outputs.length) {
      const saved = 1 - total / result.sizeBefore;
      stats.push(fmt(d.common.sizeChange, { before: formatBytes(result.sizeBefore), after: formatBytes(total) }));
      if (saved > 0.005) stats.push(fmt(d.common.reduced, { percent: formatPercent(saved) }));
    }
    stats.push(fmt(d.common.elapsed, { seconds: seconds.toFixed(1) }));

    const pdfOut = outputs.length === 1 && outputs[0].mime === MIME.pdf ? outputs[0] : null;
    const next = pdfOut ? enabledTools(OFFICE_ENABLED).filter((tool) => tool.id !== meta.id && tool.accept.includes('pdf')) : [];

    stage.replaceChildren(
      h(
        'section',
        { class: 'panel results', 'aria-live': 'polite' },
        h('div', { class: 'done-icon' }, icon('circle-check')),
        h('h2', { tabindex: -1 }, d.common.done),
        h('p', { class: 'hint' }, outputs.length > 1 ? fmt(d.common.resultFiles, { n: outputs.length }) : d.common.resultReady, ' · ', stats.join(' · ')),
        (result.messages ?? []).map((m) => note(m.text, m.tone)),
        h('ul', { class: 'result-list' }, items),
        h(
          'div',
          { class: 'result-actions' },
          primary,
          h('button', { class: 'btn btn-outline btn-lg', type: 'button', onclick: () => view && renderWorkspace(view) }, icon('arrow-left'), d.common.options),
          h('button', { class: 'btn btn-ghost btn-lg', type: 'button', onclick: () => setFiles([]) }, icon('refresh'), d.common.startOver),
        ),
        next.length > 0 && [
          h('p', { class: 'hint', style: { marginTop: '12px' } }, d.common.continueWithLabel),
          h(
            'div',
            { class: 'continue-list' },
            next.map((tool) =>
              h(
                'a',
                {
                  class: `cat-${tool.category}`,
                  href: toolPath(lang, tool.id),
                  onclick: (e: MouseEvent) => {
                    e.preventDefault();
                    setHandoff([pdfOut!]);
                    navigate(toolPath(lang, tool.id));
                  },
                },
                icon(tool.icon),
                d.tools[tool.id].name,
              ),
            ),
          ),
        ],
        h(
          'p',
          { class: 'hint', style: { marginTop: '16px' } },
          d.home.ctaTitle,
          ' ',
          h('a', { href: netserenoLink('result'), target: '_blank', rel: 'noopener' }, d.home.ctaButton),
          ' · ',
          h('a', { href: resourcesLink('result'), target: '_blank', rel: 'noopener' }, d.home.ctaResources),
        ),
      ),
    );
    (stage.querySelector('.results h2') as HTMLElement | null)?.focus();
  }

  // ---------- Page ----------
  const hero = h(
    'section',
    { class: `tool-hero cat-${meta.category}` },
    h(
      'div',
      { class: 'container' },
      h('span', { class: 'tool-icon lg', 'aria-hidden': 'true' }, icon(meta.icon)),
      h('div', null, h('nav', { class: 'breadcrumb', 'aria-label': 'breadcrumb' }, h('a', { href: homePath(lang) }, d.nav.home), ' › ', h('a', { href: `${homePath(lang)}#cat-${meta.category}` }, d.categories[meta.category])), h('h1', null, td.name), h('p', null, td.description)),
    ),
  );

  renderEmpty();

  const handoff = takeHandoff();
  if (handoff) void addFiles(handoff.map((f) => fileFromBytes(f.bytes, f.name, f.mime)));

  return {
    el: h('div', null, hero, stage),
    dispose() {
      disposed = true;
      document.removeEventListener('paste', onPaste);
      window.removeEventListener('beforeunload', beforeUnload);
      setNavigationGuard(null);
      view?.dispose?.();
      thumbs.dispose();
      for (const f of files) void releasePdf(f.bytes);
      files = [];
    },
  };
}
